import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useCriarLoteManejo } from "@/hooks/use-manejo";
import type { PropostaManejo, PropostaSetor, Pergunta, Correcao } from "@shared/manejo-proposta";
import type { MnjLoteCommit, MnjOperacaoWithItens, MnjLote } from "@shared/schema";
import { formatarFatorDesvio } from "@shared/manejo-format";
import { CheckCircle2, XCircle, AlertTriangle, HelpCircle } from "lucide-react";

const INTENCAO_LABEL: Record<PropostaManejo["intencao"], string> = {
  criar_planejamento: "Criar planejamento",
  registrar_realizacao: "Registrar realização",
  confirmar_execucao: "Confirmar execução",
  ajustar: "Ajustar",
  consultar: "Consultar histórico",
};

// A proposta é dado puro (sem frase pronta) — este é o renderizador React (card do
// Hugo). Um futuro renderizador de WhatsApp (Agente da plataforma) monta seu próprio
// texto a partir dos MESMOS campos estruturados. Ver Rizoma_Agente_Whatsapp_Arquitetura.md §3.
function formatarPergunta(p: Pergunta): string {
  const ctx = p.contexto ?? {};
  switch (p.tipo) {
    case "data_ambigua":
      return `Não entendi a data "${ctx.textoOriginal ?? ""}". Qual é a data prevista?`;
    case "setor_nao_encontrado":
      return `Não encontrei o setor "${p.setorTexto}" entre os setores fertirrigáveis cadastrados.`;
    case "produto_nao_encontrado":
      return `Não reconheço "${p.produtoTexto}" (setor ${p.setorTexto}). A qual produto cadastrado corresponde?`;
    case "escopo_produtos": {
      const faltantes = Array.isArray(ctx.faltantes) ? ctx.faltantes.join(", ") : "";
      return `"${p.setorTexto}" veio com ${ctx.produtosInformados} de ${ctx.produtosPrograma} produto(s) do programa "${ctx.programaNome}". É só isso mesmo ou completar com os que faltam (${faltantes})?`;
    }
    case "cobertura_setores": {
      const faltando = Array.isArray(ctx.faltando) ? ctx.faltando.join(", ") : "";
      return `Cobre ${ctx.informados} dos ${ctx.esperados} setores fertirrigáveis cadastrados — faltam ${faltando}. Intencional (aplicação parcial)?`;
    }
    default:
      return "Confirme este item.";
  }
}

function formatarCorrecao(c: Correcao): string {
  const ctx = c.contexto ?? {};
  if (c.tipo === "razao_produtos") {
    const razaoAtual = Number(ctx.razaoAtual ?? 0);
    const mediana = Number(ctx.medianaGrupo ?? 0);
    const fator = formatarFatorDesvio(razaoAtual, mediana) ?? "?";
    return `${c.setorTexto}: razão ${c.produtoTexto}/${ctx.outroProdutoTexto} está ${fator} fora da curva dos demais setores → sugerido ${c.valorSugerido}.`;
  }
  if (c.tipo === "dose_fora_curva") {
    const fonte = ctx.fonte === "programa" ? "alvo do programa" : "histórico do setor";
    return `${c.setorTexto}: ${c.produtoTexto} veio ${c.valorAtual}kg, ${ctx.desvioPercentual}% fora do ${fonte} (${c.valorSugerido}kg).`;
  }
  return `${c.setorTexto}: ${c.produtoTexto} → sugerido ${c.valorSugerido}.`;
}

interface PropostaCardProps {
  proposta: PropostaManejo;
  onConfirmado?: (resultado: { lote: MnjLote; operacoes: MnjOperacaoWithItens[] }) => void;
}

// Card de confirmação — o portão universal (Rizoma_Nutricao_Fase0_Spec.md §5).
// Usado tanto pela entrada de texto livre quanto pelas respostas do agente que
// propõem uma ação composta (mesmo componente, mesmo caminho de commit já testado).
export function PropostaCard({ proposta, onConfirmado }: PropostaCardProps) {
  const { toast } = useToast();
  const [propostaLocal, setPropostaLocal] = useState(proposta);
  const [respostas, setRespostas] = useState<Record<string, string>>({});
  const [correcoesAplicadas, setCorrecoesAplicadas] = useState<Set<string>>(new Set());
  const [dataOverride, setDataOverride] = useState<string>("");
  const [confirmado, setConfirmado] = useState(false);

  const criarLote = useCriarLoteManejo();

  const responderPerguntaComEfeito = (pergunta: Pergunta, valor: string) => {
    setRespostas(r => ({ ...r, [pergunta.id]: valor }));
    if (pergunta.tipo === "escopo_produtos" && valor === "completar_programa") {
      setPropostaLocal(p => ({
        ...p,
        setores: p.setores.map(s => s.setorTexto !== pergunta.setorTexto ? s : {
          ...s,
          produtos: [...s.produtos, ...(s.itensProgramaSugeridos ?? [])],
          itensProgramaSugeridos: undefined,
        }),
      }));
    }
    if (pergunta.tipo === "setor_nao_encontrado") {
      setPropostaLocal(p => ({
        ...p,
        setores: p.setores.map(s => s.setorTexto === pergunta.setorTexto ? { ...s, setorId: valor } : s),
      }));
    }
    if (pergunta.tipo === "produto_nao_encontrado") {
      setPropostaLocal(p => ({
        ...p,
        setores: p.setores.map(s => s.setorTexto !== pergunta.setorTexto ? s : {
          ...s,
          produtos: s.produtos.map(it => it.produtoTexto === pergunta.produtoTexto ? { ...it, mnjProdutoId: valor } : it),
        }),
      }));
    }
  };

  const aplicarCorrecao = (correcaoId: string, aplicar: boolean) => {
    setCorrecoesAplicadas(prev => {
      const next = new Set(prev);
      if (aplicar) next.add(correcaoId); else next.delete(correcaoId);
      return next;
    });
  };

  const aceitarTodasCorrecoes = () => {
    setCorrecoesAplicadas(new Set(propostaLocal.correcoes.map(c => c.id)));
  };

  // Aplica correções aceitas no kgTotal exibido/enviado.
  const setoresComCorrecoes = useMemo<PropostaSetor[]>(() => {
    return propostaLocal.setores.map(s => ({
      ...s,
      produtos: s.produtos.map(it => {
        if (it.correcaoId && correcoesAplicadas.has(it.correcaoId)) {
          const correcao = propostaLocal.correcoes.find(c => c.id === it.correcaoId);
          if (correcao) return { ...it, kgTotal: correcao.valorSugerido };
        }
        return it;
      }),
    }));
  }, [propostaLocal, correcoesAplicadas]);

  const perguntasPendentes = useMemo(() => {
    return propostaLocal.perguntas.filter(p => !(p.id in respostas) && !(p.tipo === "data_ambigua" && dataOverride));
  }, [propostaLocal, respostas, dataOverride]);

  const podeConfirmar =
    !confirmado &&
    perguntasPendentes.length === 0 &&
    setoresComCorrecoes.every(s => s.setorId && s.produtos.every(p => p.mnjProdutoId && p.kgTotal !== null && p.kgTotal > 0));

  const handleConfirmar = async () => {
    if (!podeConfirmar) return;
    // Texto livre resolve 1 setor por operação — expandirSetoresTexto já achatou
    // qualquer lista/"+"; múltiplos setores por operação é a capacidade do modelo
    // (Sprint 1 §1.2c), não o que este card produz (ver orchestrator.ts, mesma regra).
    const payload: MnjLoteCommit = {
      origem: propostaLocal.origem,
      entradaRaw: propostaLocal.entradaRaw,
      operacoes: setoresComCorrecoes.map(s => ({
        dataPrevista: dataOverride || s.dataPrevista!,
        setores: [{ setorId: s.setorId!, percentualAplicado: s.percentualAplicado }],
        // `kgTotal` é o vocabulário da PROPOSTA (o interpretador só fala de
        // fertirrigação); `qtdPrevista` é o do modelo, na unidade do produto.
        // A tradução é aqui, no commit — genericizar o Comando é o ciclo 5.
        itens: s.produtos.map(p => ({
          mnjProdutoId: p.mnjProdutoId!,
          qtdPrevista: p.kgTotal!,
          preenchidoPeloPrograma: p.preenchidoPeloPrograma,
        })),
      })),
    };
    try {
      const resultado = await criarLote.mutateAsync(payload);
      toast({ title: `${resultado.operacoes.length} operação(ões) criada(s)!` });
      setConfirmado(true);
      onConfirmado?.(resultado);
    } catch (e: any) {
      toast({ title: "Erro ao confirmar", description: e.message, variant: "destructive" });
    }
  };

  if (confirmado) {
    return (
      <div className="rounded-lg border bg-sucesso-suave border-sucesso/30 p-3 text-sm text-sucesso flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        Operações criadas no kanban.
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card p-4 space-y-4">
      <div className="flex items-center justify-between">
        <Badge variant="secondary">{INTENCAO_LABEL[propostaLocal.intencao]}</Badge>
        {propostaLocal.correcoes.length > 0 && (
          <Button variant="outline" size="sm" onClick={aceitarTodasCorrecoes}>
            Aceitar tudo (correções)
          </Button>
        )}
      </div>

      {/* Perguntas — sem default, exigem resposta */}
      {propostaLocal.perguntas.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold flex items-center gap-1.5">
            <HelpCircle className="h-4 w-4 text-atencao" /> Perguntas
          </p>
          {propostaLocal.perguntas.map(p => (
            <div key={p.id} className="rounded-md border border-atencao/30 bg-atencao-suave p-3 space-y-2">
              <p className="text-sm">{formatarPergunta(p)}</p>
              {p.tipo === "data_ambigua" ? (
                <Input type="date" value={dataOverride} onChange={e => setDataOverride(e.target.value)} className="w-40" />
              ) : p.opcoes ? (
                <div className="flex flex-wrap gap-1.5">
                  {p.opcoes.map(o => (
                    <Button
                      key={o.valor}
                      size="sm"
                      variant={respostas[p.id] === o.valor ? "default" : "outline"}
                      onClick={() => responderPerguntaComEfeito(p, o.valor)}
                    >
                      {o.label}
                    </Button>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {/* Correções — têm valor sugerido, aceitam aplicar/rejeitar */}
      {propostaLocal.correcoes.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4 text-atencao" /> Correções sugeridas
          </p>
          {propostaLocal.correcoes.map(c => (
            <div key={c.id} className="rounded-md border border-atencao/30 bg-atencao-suave p-3 flex items-center justify-between gap-3">
              <p className="text-sm flex-1">{formatarCorrecao(c)}</p>
              <div className="flex gap-1.5 shrink-0">
                <Button
                  size="sm"
                  variant={correcoesAplicadas.has(c.id) ? "default" : "outline"}
                  onClick={() => aplicarCorrecao(c.id, true)}
                >
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Aplicar
                </Button>
                <Button size="sm" variant="ghost" onClick={() => aplicarCorrecao(c.id, false)}>
                  <XCircle className="h-3.5 w-3.5 mr-1" />Rejeitar
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Setores resolvidos */}
      <div className="space-y-2">
        <p className="text-sm font-semibold">Setores ({setoresComCorrecoes.length})</p>
        {setoresComCorrecoes.map((s, idx) => (
          <div key={idx} className="rounded-md border p-3 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm">
                {s.setorTexto}
                {!s.setorId && <Badge variant="destructive" className="ml-2 text-[10px]">não resolvido</Badge>}
              </span>
              <span className="text-xs text-muted-foreground">
                {dataOverride || s.dataPrevista} · {s.percentualAplicado}%
              </span>
            </div>
            <div className="space-y-1">
              {s.produtos.map((p, pIdx) => (
                <div key={pIdx} className="flex items-center justify-between text-sm">
                  <span className={p.preenchidoPeloPrograma ? "text-muted-foreground italic" : ""}>
                    {p.produtoTexto}
                    {!p.mnjProdutoId && <Badge variant="destructive" className="ml-2 text-[10px]">?</Badge>}
                    {p.preenchidoPeloPrograma && <span className="text-[10px] ml-1">(programa)</span>}
                  </span>
                  <span className="tabular-nums">{p.kgTotal !== null ? `${p.kgTotal} kg` : "—"}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <p className="text-[11px] text-muted-foreground italic">números calculados pelo módulo, não pelo modelo</p>

      <div className="flex justify-end pt-1">
        <Button onClick={handleConfirmar} disabled={!podeConfirmar || criarLote.isPending} size="lg">
          {criarLote.isPending ? "Criando..." : "Confirmar e criar operações"}
        </Button>
      </div>
    </div>
  );
}
